import { Box, IconButton, Paper, Typography } from "@mui/material"
import { EditOutlined } from "@mui-symbols-material/w300"
import { type FC, useState } from "react"

import { PackagingWarehouseInstructionsFormDialog } from "./PackagingWarehouseInstructionsFormDialog/PackagingWarehouseInstructionsFormDialog"
import type { PackagingWarehouseInstructionsFormValues } from "./schemas"

import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { useUpdateAssociationLocation } from "@/screens/CustomerProducts/hooks/useUpdateAssociationLocation"
import { useProductPreferenceAssociationsStore } from "@/screens/CustomerProducts/stores/useProductPreferenceAssociationsStore"

interface PackagingWarehouseInstructionsSectionProps {
  companyProductAssociationLocationId: string
  companyId: string
  productId: number
  warehouseInstructions: string | null
  bolNotes: string | null
}

export const PackagingWarehouseInstructionsSection: FC<PackagingWarehouseInstructionsSectionProps> = ({
  companyProductAssociationLocationId,
  companyId,
  productId,
  warehouseInstructions,
  bolNotes,
}) => {
  const { hasPermissions } = usePermissionManager()
  const canEdit = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const { updateLocation, isUpdating } = useUpdateAssociationLocation()
  const executeSearch = useProductPreferenceAssociationsStore(({ executeSearch }) => executeSearch)

  const initialValues: PackagingWarehouseInstructionsFormValues = {
    warehouseInstructions: warehouseInstructions ?? "",
    bolNotes: bolNotes ?? "",
  }

  const handleEditOpen = () => setIsDialogOpen(true)
  const handleDialogClose = () => setIsDialogOpen(false)

  const handleSubmit = async (formValues: PackagingWarehouseInstructionsFormValues) => {
    const success = await updateLocation({
      id: companyProductAssociationLocationId,
      warehouseInstructions: formValues.warehouseInstructions,
      bolNotes: formValues.bolNotes,
    })
    if (success) {
      handleDialogClose()
      executeSearch(queryClient, { companyId, productId }, true)
    }
  }

  return (
    <>
      <Paper
        variant='outlined'
        elevation={0}
        className='flex flex-col gap-y-6 border-gray-400 p-6'
        data-testid='customer-product.packaging-warehouse-section'
      >
        <Box className='flex items-center justify-between gap-4'>
          <Typography variant='subtitle1' component='h3' className='text-ml text-gray-900'>
            Packaging & Warehouse Instructions
          </Typography>
          {canEdit && (
            <IconButton onClick={handleEditOpen} data-testid='customer-product.packaging-warehouse-section.edit-button'>
              <EditOutlined color='primary' />
            </IconButton>
          )}
        </Box>
        <Box className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          <Box className='flex flex-col gap-y-2'>
            <Typography variant='subtitle1' className='font-medium text-gray-900'>
              BoL Notes
            </Typography>
            <Typography variant='body2' className='text-gray-700'>
              Special Packaging Notes
            </Typography>
            {bolNotes && (
              <Typography
                variant='body2'
                className='whitespace-pre-wrap font-normal text-gray-900'
                data-testid='customer-product.packaging-warehouse-section.bol-notes-value'
              >
                {bolNotes}
              </Typography>
            )}
          </Box>
          <Box className='flex flex-col gap-y-2'>
            <Typography variant='subtitle1' className='font-medium text-gray-900'>
              Warehouse Instructions
            </Typography>
            <Typography variant='body2' className='text-gray-700'>
              Pick / Stage / Ship Notes
            </Typography>
            {warehouseInstructions && (
              <Typography
                variant='body2'
                className='whitespace-pre-wrap font-normal text-gray-900'
                data-testid='customer-product.packaging-warehouse-section.warehouse-instructions-value'
              >
                {warehouseInstructions}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
      {isDialogOpen && (
        <PackagingWarehouseInstructionsFormDialog
          initialValues={initialValues}
          isUpdating={isUpdating}
          onClose={handleDialogClose}
          onSubmit={handleSubmit}
        />
      )}
    </>
  )
}
